<template>
    <div>
        <div
            v-if="config && !config.active"
            class="kgr-alert kgr-alert--info kgr-row kgr-row--bigMargin"
        >
            <div class="kgr-row kgr-text">
                {{ $t('Die Online-Beitrittsanträge sind für diesen Verein noch nicht eingerichtet.') }}
            </div>
            <a
                :href="applicationSettingsUrl"
                class="kgr-button kgr-button--outline"
            >
                {{ $t('Jetzt einrichten') }}
            </a>
        </div>

        <div v-else>
            <div class="kgr-tabs">
                <div class="kgr-tabs__headers kgr-row kgr-row--bigMargin">
                    <a
                        class="kgr-clickable kgr-tabs__tab"
                        :class="{ 'is-active' : isActive}"
                        @click="loadActive"
                    >
                        {{ $t('Offene Anträge') }}
                        <span
                            class="kgr-tabs__badge kgr-badge"
                            :class="{ 'kgr-badge--highlight' : activeApplicationsCount > 0, 'kgr-badge--info' : activeApplicationsCount === 0 }"
                        >
                            {{ activeApplicationsCount }}
                        </span>
                    </a>
                    <a
                        class="kgr-clickable kgr-tabs__tab"
                        :class="{ 'is-active' : isRejected}"
                        @click="loadRejected"
                    >
                        {{ $t('club.application.navigation.deleted') }}
                    </a>
                    <a
                        class="kgr-clickable kgr-tabs__tab"
                        :class="{ 'is-active' : isAccepted}"
                        @click="loadAccepted"
                    >
                        {{ $t('Übernommene Anträge') }}
                    </a>
                </div>
            </div>

            <div class="kgr-postfix kgr-postfix--gutters">
                <div class="kgr-postfix__fixed animated fadeIn">
                    <label class="kgr-inputGroup">
                        <div
                            v-t="'sort'"
                            class="kgr-inputGroup__label"
                        ></div>
                        <kgr-sort-select
                            :tab="status"
                            :value="sort"
                            :show-accepted-date="isAccepted"
                            :show-rejected-date="isRejected"
                            @post-input="updateSort"
                        />
                    </label>
                </div>
                <div class="kgr-postfix__fixed animated fadeIn">
                    <kgr-multi-select
                        v-model="selectedDepartments"
                        :options="departments"
                        :disabled="departments.length === 0"
                        :label="$t('Abteilungen')"
                        :inline="false"
                        searchable
                    />
                </div>
                <div class="kgr-postfix__fixed animated fadeIn">
                    <label class="kgr-inputGroup">
                        <div class="kgr-inputGroup__label">
                            {{ $t('Quelle') }}
                        </div>
                        <kgr-generic-select
                            :options="sourceOptions"
                            :value="selectedSource"
                            option-key="value"
                            display="label"
                            default-text="Alle Quellen"
                            @post-input="onSourceSelected"
                        />
                    </label>
                </div>
                <div
                    v-if="isMemberAppSourceSelected"
                    class="kgr-postfix__fixed animated fadeIn"
                >
                    <label class="kgr-inputGroup">
                        <div class="kgr-inputGroup__label">
                            {{ $t('Antragsart') }}
                        </div>
                        <kgr-generic-select
                            :options="typeOptions"
                            :value="selectedType"
                            option-key="value"
                            display="label"
                            default-text="Alle Antragsarten"
                            @post-input="onTypeSelected"
                        />
                    </label>
                </div>
            </div>

            <div>
                <br />
                <kgr-loading v-if="loading" />
                <div v-else>
                    <kgr-empty-data
                        v-if="visibleApplications.length === 0"
                        :value="$t('Es wurden keine Ergebnisse gefunden.')"
                    />

                    <div v-else>
                        <table class="kgr-table kgr-table--striped kgr-table--block kgr-row">
                            <thead>
                                <tr class="kgr-table__row">
                                    <th
                                        class="kgr-table__cell kgr-table__cell--headline"
                                        style="width: 10%"
                                    >
                                        {{ $t('Bewerbung vom') }}
                                    </th>
                                    <th class="kgr-table__cell kgr-table__cell--headline">
                                        {{ memberApplicationHeader }}
                                    </th>
                                    <th
                                        class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions"
                                        style="width: 10%"
                                    >
                                        {{ $t('Aktionen') }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="application in visibleApplications"
                                    :key="application.id"
                                    class="kgr-table__row kgr-table__row--top"
                                >
                                    <td style="width: 10%;">
                                        <template v-if="application.doneAt">
                                            {{ application.doneAt }}
                                        </template>
                                        <template v-else>
                                            {{ application.createdAt }} <kgr-info-icon
                                                :title="$t('Dies ist der Zeitpunkt, zu dem der Antrag vom Antragssteller gestartet wurde. In neueren Anträgen steht hier das Datum, zu dem der Antrag abgeschickt wurde.')"
                                                small
                                                class="info-icon-aligned"
                                            />
                                        </template>
                                        <div
                                            v-if="applicationTypeLabel(application)"
                                            class="kgr-text kgr-text--small"
                                        >
                                            {{ applicationTypeLabel(application) }}
                                        </div>
                                        <div
                                            v-if="applicationSourceLabel(application)"
                                            class="kgr-text kgr-text--small kgr-color kgr-color--gray"
                                        >
                                            {{ applicationSourceLabel(application) }}
                                        </div>
                                    </td>
                                    <td>
                                        <kgr-accordion class="kgr-row kgr-row--noneIfLastChild">
                                            <kgr-accordion-item
                                                v-for="applicationMember in application.applicationMembers"
                                                :key="applicationMember.id"
                                                :open="false"
                                                @opened="applicationMember.open = true"
                                                @closed="applicationMember.open = false"
                                            >
                                                <template #headerHeadline>
                                                    <template v-if="applicationMember.isPayer && !applicationMember.isMember">
                                                        {{ applicationMember.getFullName() }}
                                                        ({{ $t('Nur Zahler') }})
                                                    </template>
                                                    <template v-else>
                                                        <span :class="{ 'kgr-text kgr-text--bold': isMainApplicationMember(application, applicationMember) }">
                                                            {{ applicationMember.getFullName() }}
                                                        </span>
                                                        <template v-if="applicationMember.contractStart">
                                                            | {{ applicationMember.contractStart }}
                                                        </template>
                                                        <template v-else>
                                                            | <span class="kgr-color kgr-color--error">
                                                                {{ $t('Bitte Vertragsstart auswählen') }}
                                                            </span>
                                                        </template>

                                                        <template v-if="applicationMember.departments?.length > 0">
                                                            | {{ applicationMember.departments.map(d => d.name).join(', ') }}
                                                        </template>
                                                        <template v-else-if="config && config.departments?.length > 0">
                                                            | -
                                                        </template>
                                                    </template>
                                                </template>

                                                <template #header-left>
                                                    <template v-if="applicationMember.hasFilesUploadedByMember">
                                                        <kgr-icon
                                                            icon-class="attach-file"
                                                            small
                                                            class="kgr-button__icon"
                                                        />
                                                    </template>
                                                </template>

                                                <div
                                                    v-if="applicationMember.member"
                                                    class="kgr-alert kgr-alert--info"
                                                >
                                                    <div
                                                        v-if="!applicationMember.isMember"
                                                        class="kgr-row kgr-text kgr-text--italic"
                                                    >
                                                        {{ $t('Dieses Mitglied ist hier nur als Zahler (Rechnungsempfänger) aufgeführt.') }}
                                                    </div>
                                                    <div
                                                        v-else-if="application.type === 'upgrade'"
                                                        class="kgr-row kgr-text kgr-text--italic"
                                                    >
                                                        {{ $t('Es handelt sich um einen Upgrade-Antrag für dieses Mitglied.') }}
                                                    </div>
                                                    <a
                                                        :href="urlForMember(applicationMember.member)"
                                                        class="kgr-button kgr-button--outline"
                                                    >
                                                        <kgr-icon
                                                            icon-class="user"
                                                            class="kgr-button__icon kgr-button__icon--left kgr-icon"
                                                        />
                                                        {{ $t('Kurzprofil anzeigen') }}
                                                    </a>
                                                </div>

                                                <div v-if="config && applicationMember.open">
                                                    <kgr-application-member
                                                        :member="applicationMember"
                                                        :invoice-receiver-name="divergentInvoiceReceiverName(application, applicationMember)"
                                                        :club-id="clubId"
                                                        :config="config"
                                                        :editable="application.isEditable"
                                                        :status="status"
                                                    />
                                                </div>
                                            </kgr-accordion-item>
                                            <kgr-accordion-item
                                                v-for="applicationDog in application.applicationDogs"
                                                :key="applicationDog.id"
                                                :title="applicationDog.name + ' | ' + $t('Zugehöriger Hund')"
                                                :open="false"
                                                @opened="applicationDog.open = true"
                                                @closed="applicationDog.open = false"
                                            >
                                                <div v-if="config && applicationDog.open">
                                                    <kgr-application-dog
                                                        :dog="applicationDog"
                                                        :members="application.applicationMembers"
                                                        :club-id="clubId"
                                                        :editable="isActive && application.isEditable"
                                                    />
                                                </div>
                                            </kgr-accordion-item>
                                        </kgr-accordion>
                                        <div
                                            v-if="application.createdBy"
                                        >
                                            {{ $t('Antrag eingereicht von') }}
                                            <a
                                                :href="urlForMember(application.createdBy.id)"
                                                class="kgr-link kgr-link--unobtrusive"
                                            >
                                                {{ application.createdBy.name }}
                                            </a>
                                        </div>
                                    </td>
                                    <td
                                        class="content"
                                        style="width: 10%;"
                                    >
                                        <div v-if="isActive">
                                            <template v-if="application.isEditable">
                                                <div class="kgr-tooltip kgr-tooltip--bottom kgr-tooltip--centered kgr-hidden-print">
                                                    <a
                                                        class="kgr-table__action kgr-tooltip__trigger"
                                                        @click="acceptApplication(application)"
                                                    >
                                                        <kgr-icon
                                                            icon-class="check"
                                                        />
                                                    </a>
                                                    <span class="kgr-tooltip__content kgr-region kgr-region--gray">
                                                        {{ $t('club.member.application.accept.title') }}
                                                    </span>
                                                </div>

                                                <div class="kgr-tooltip kgr-tooltip--bottom kgr-tooltip--centered kgr-hidden-print">
                                                    <a
                                                        class="kgr-table__action kgr-tooltip__trigger"
                                                        @click="rejectApplication(application)"
                                                    >
                                                        <kgr-icon
                                                            icon-class="block"
                                                        />
                                                    </a>
                                                    <span class="kgr-tooltip__content kgr-region kgr-region--gray">
                                                        {{ $t('club.member.application.reject.title') }}
                                                    </span>
                                                </div>
                                            </template>
                                        </div>
                                        <div v-else-if="isAccepted">
                                            {{ $t('akzeptiert am:') }} {{ application.acceptedAt }}
                                            <div v-if="application.lastStatusChangeBy">
                                                {{ $t('akzeptiert von:') }} {{ application.lastStatusChangeBy }}
                                            </div>
                                        </div>
                                        <div v-else>
                                            {{ $t('abgelehnt am:') }} {{ application.rejectedAt }}
                                            <div v-if="application.lastStatusChangeBy">
                                                {{ $t('abgelehnt von:') }} {{ application.lastStatusChangeBy }}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <kgr-paginator
                v-if="isActive"
                v-model="paginatorActive"
                :length="applicationsCount"
                @post-input="load()"
            />
            <kgr-paginator
                v-if="isRejected"
                v-model="paginatorRejected"
                :length="applicationsCount"
                @post-input="load()"
            />
            <kgr-paginator
                v-if="isAccepted"
                v-model="paginatorAccepted"
                :length="applicationsCount"
                @post-input="load()"
            />

            <kgr-confirm
                ref="confirmReject"
                :headline="$t('club.member.application.reject.title')"
            >
                <template v-if="applicationToReject">
                    {{ $t('club.member.application.reject.message', {'name': getMainApplicationMemberName(applicationToReject)}) }}
                </template>
            </kgr-confirm>
        </div>
    </div>
</template>
<script>

import Accordion from '../../components/common/accordion/Accordion.vue';
import AccordionItem from '../../components/common/accordion/AccordionItem.vue';
import ApplicationDog from './ApplicationDog.vue';
import ApplicationMember from './ApplicationMember.vue';
import ConfirmDialog from '../../components/common/modal/ConfirmDialog.vue';
import EmptyData from '../../components/common/EmptyData.vue';
import EventBus from '../../js/service/EventBus.js';
import GenericSelect from '../../components/common/form/select/GenericSelect.vue';
import Icon from '../../components/common/icon/Icon.vue';
import IconInfo from '../../components/common/icon/IconInfo.vue';
import KgrIcon from '@legacy/components/common/icon/Icon.vue';
import Loading from '../../components/common/Loading.vue';
import MemberApplication from '../../js/dto/MemberApplication.js'
import MemberApplicationConfigurationRepository from '../../js/repository/MemberApplicationConfigurationRepository.js';
import MemberApplicationRepository from '../../js/repository/MemberApplicationRepository.js';
import MemberApplicationSortSelect from './MemberApplicationSortSelect.vue';
import MultiSelect from '../../components/common/form/select/MultiSelect.vue';
import Paginator from '../../components/common/Paginator.vue'
import UrlMixin from '../../components/members/UrlMixin.vue';

const INIT_LIMIT = 25;

export default {
    components: {
        KgrIcon,
        'kgr-accordion': Accordion,
        'kgr-accordion-item': AccordionItem,
        'kgr-application-dog': ApplicationDog,
        'kgr-application-member': ApplicationMember,
        'kgr-confirm': ConfirmDialog,
        'kgr-empty-data': EmptyData,
        'kgr-generic-select': GenericSelect,
        'kgr-icon': Icon,
        'kgr-loading': Loading,
        'kgr-info-icon': IconInfo,
        'kgr-multi-select': MultiSelect,
        'kgr-paginator': Paginator,
        'kgr-sort-select': MemberApplicationSortSelect,
    },
    mixins: [UrlMixin],
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        applicationSettingsUrl: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            sortActive: 'entryDate',
            sortRejected: 'entryDate',
            sortAccepted: 'entryDate',
            loading: true,
            status: MemberApplication.STATUS_ACTIVE,
            applicationToReject: null,
            activeApplicationsCount: 0,
            applicationsCount: 0,
            activeApplications: [],
            rejectedApplications: [],
            acceptedApplications: [],
            config: null,
            selectedDepartments: [],
            selectedSource: null,
            selectedType: null,
            paginatorActive: {
                limit: INIT_LIMIT,
                offset: 0,
            },
            paginatorRejected: {
                limit: INIT_LIMIT,
                offset: 0,
            },
            paginatorAccepted: {
                limit: INIT_LIMIT,
                offset: 0,
            },
            initialActiveSort: true,
            initialRejectedSort: true,
            initialAcceptedSort: true,
        };
    },
    computed: {
        memberApplicationHeader() {
            let title = this.$t('Bewerbung von')  + ' | ' + this.$t('Gewünschtes Eintrittsdatum');
            if (this.config && this.config.departments?.length > 0) {
                title += ' | ' + this.$t('Gewünschte Abteilungen');
            }
            return title;
        },
        isActive() {
            return this.status === MemberApplication.STATUS_ACTIVE;
        },
        isRejected() {
            return this.status === MemberApplication.STATUS_REJECTED;
        },
        isAccepted() {
            return this.status === MemberApplication.STATUS_ACCEPTED;
        },
        visibleApplications() {
            if (this.isActive) {
                return this.activeApplications;
            } else if (this.isAccepted) {
                return this.acceptedApplications;
            }
            return this.rejectedApplications;
        },
        activeFilters() {
            return {
                departmentIds: this.selectedDepartments.map(department => department.value),
                source: this.selectedSource?.value ?? null,
                type: this.isMemberAppSourceSelected ? (this.selectedType?.value ?? null) : null,
            };
        },
        sourceOptions() {
            return [
                { value: 'member_app', label: this.$t('Vereins-App') },
                { value: 'registration_app', label: this.$t('Online-Bewerbung') },
            ];
        },
        typeOptions() {
            return [
                { value: 'upgrade', label: this.$t('Upgrade-Antrag') },
                { value: 'new', label: this.$t('Neuantrag') },
            ];
        },
        isMemberAppSourceSelected() {
            return this.selectedSource?.value === 'member_app';
        },
        departments() {
            if (!this.config || !this.config.departments) {
                return [];
            }
            return this.config.departments.map(department => {
                return {
                    'label': department.name,
                    'value': department.id
                };
            });
        },
        sort() {
            if (this.isActive) {
                return this.sortActive;
            } else if (this.isRejected) {
                return this.sortRejected;
            } else if (this.isAccepted) {
                return this.sortAccepted;
            }
            return 'entryDate';
        },
        limit() {
            if (this.isActive) {
                return this.paginatorActive.limit;
            } else if (this.isRejected) {
                return this.paginatorRejected.limit;
            } else if (this.isAccepted) {
                return this.paginatorAccepted.limit;
            }
            return 0;
        },
        offset() {
            if (this.isActive) {
                return this.paginatorActive.offset;
            } else if (this.isRejected) {
                return this.paginatorRejected.offset;
            } else if (this.isAccepted) {
                return this.paginatorAccepted.offset;
            }
            return 0;
        },
    },
    watch: {
        selectedDepartments() {
            this.onFilterChange();
        },
    },
    mounted() {
        this.sortActive = this.readStoredSort(MemberApplication.STATUS_ACTIVE);
        this.sortRejected = this.readStoredSort(MemberApplication.STATUS_REJECTED);
        this.sortAccepted = this.readStoredSort(MemberApplication.STATUS_ACCEPTED);
        this.loadConfig();
        this.refreshActiveApplicationsCount();
        this.loadActive();
    },
    methods: {
        readStoredSort(tab) {
            let value = localStorage.getItem('kgr:member-application-sort-select:' + tab);
            if (value === 'createDate') {
                value = null; // legacy sort option - no longer supported
            }
            return value || 'entryDate';
        },
        async rejectApplication(application) {
            this.applicationToReject = application;
            const confirmed = await this.$refs.confirmReject.open();
            if (confirmed) {
                this.loading = true;
                MemberApplicationRepository.rejectApplication(application.id)
                    .then(() => {
                        this.$notify.success('Der Antrag wurde abgelehnt.');
                        this.refreshActiveApplicationsCount();
                        this.loadActive();
                    })
                    .catch(() => {
                        this.$notify.error('saveChanges.error');
                        this.loading = false;
                    });
            }
            this.applicationToReject = null;
        },
        acceptApplication(application) {
            this.loading = true;
            MemberApplicationRepository.acceptApplication(application.id)
                .then(() => {
                    const message =
                        application.type === 'upgrade'
                            ? this.$t('Die Änderungen am Datensatz wurden übernommen.')
                            : this.$t('Der Antrag wurde angenommen. Ein neues Mitglied wurde erstellt.');
                    this.$notify.success(message);
                    this.refreshActiveApplicationsCount();
                    this.loadActive();
                })
                .catch(() => {
                    this.$notify.error('saveChanges.error');
                    this.loading = false;
                });
        },
        loadActive() {
            this.status = MemberApplication.STATUS_ACTIVE;
            this.load();
        },
        loadRejected() {
            this.status = MemberApplication.STATUS_REJECTED;
            this.load();
        },
        loadAccepted() {
            this.status = MemberApplication.STATUS_ACCEPTED;
            this.load();
        },
        load() {
            this.loading = true;
            MemberApplicationRepository.getCountAndMemberApplicationsByClubIdAndFilters(this.clubId, this.status, this.sort, this.limit, this.offset, this.activeFilters)
                .then(response => {
                    this.applicationsCount = response[0];
                    if (this.isActive) {
                        this.activeApplications = response[1];
                    } else if (this.isRejected) {
                        this.rejectedApplications = response[1];
                    } else if (this.isAccepted) {
                        this.acceptedApplications = response[1];
                    }
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false);
        },
        refreshActiveApplicationsCount() {
            MemberApplicationRepository.countByClub(this.clubId, MemberApplication.STATUS_ACTIVE)
                .then(count => {
                    this.activeApplicationsCount = count;
                    EventBus.$emit('updatedCounterBadge', {name: 'memberApplicationBadge', value: this.activeApplicationsCount});
                })
                .catch(() => this.$notify.error('error.loadingData'));
        },
        onFilterChange() {
            if (this.isActive) {
                this.paginatorActive.offset = 0;
            } else if (this.isRejected) {
                this.paginatorRejected.offset = 0;
            } else if (this.isAccepted) {
                this.paginatorAccepted.offset = 0;
            }
            this.load();
        },
        loadConfig() {
            MemberApplicationConfigurationRepository.findByClubId(this.clubId)
                .then(config => this.config = config)
                .catch(() => this.$notify.error('error.loadingData'));
        },
        updateSort(sort) {
            let reload = true;
            if (this.isActive) {
                reload = !this.initialActiveSort && this.sortActive !== sort;
                this.sortActive = sort;
                this.initialActiveSort = false;
            } else if (this.isRejected) {
                reload = !this.initialRejectedSort && this.sortRejected !== sort;
                this.sortRejected = sort;
                this.initialRejectedSort = false;
            } else if (this.isAccepted) {
                reload = !this.initialAcceptedSort && this.sortAccepted !== sort;
                this.sortAccepted = sort;
                this.initialAcceptedSort = false;
            }
            if (reload) {
                this.load();
            }
        },
        onSourceSelected(source) {
            this.selectedSource = source;
            if (!this.isMemberAppSourceSelected) {
                this.selectedType = null;
            }
            this.onFilterChange();
        },
        onTypeSelected(type) {
            this.selectedType = type;
            this.onFilterChange();
        },
        applicationTypeLabel(application) {
            if (application.type === 'upgrade') {
                return this.$t('Upgrade-Antrag');
            } else {
                return this.$t('Neuantrag');
            }
        },
        applicationSourceLabel(application) {
            if (application.source === 'member_app') {
                return this.$t('Vereins-App');
            } else {
                return this.$t('Online-Bewerbung');
            }
        },
        getMainApplicationMemberName(applicationToReject) {
            const applicationMembers = applicationToReject.applicationMembers;
            const payerMembers = applicationMembers.filter(member => member.isPayer);
            let mainApplicationMember;
            if (payerMembers.length > 0) {
                mainApplicationMember = payerMembers[0]
            } else {
                mainApplicationMember = applicationMembers[0];
            }

            return mainApplicationMember.firstname + ' ' + mainApplicationMember.lastname;
        },
        divergentInvoiceReceiverName(application, applicationMember) {
            if (!applicationMember.isMember || applicationMember.isPayer) {
                return null;
            }
            const receiver = application.applicationMembers.find(iterationApplicationMember => iterationApplicationMember.isPayer && iterationApplicationMember.id !== applicationMember.id);

            return receiver ? `${receiver.firstname} ${receiver.lastname}` : null;
        },
        isMainApplicationMember(application, applicationMember) {
            const memberCount = application.applicationMembers.filter(member => member.isMember).length;
            if (memberCount > 1) {
                return applicationMember.isPayer;
            }

            return applicationMember.isMember;
        },
    },
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">
    .view_left {
        float: left;
        width: 30%
    }
    .view_right {
      float: left;
    }
    .container:after {
      content: "";
      display: table;
      clear: both;
    }
    .properties {
        width: 90%;
        column-count: 2;
        column-gap: 20px;
    }
    td {
        padding: 10px;
        vertical-align: top;
    }
    td a {
        cursor: pointer;
    }
    svg.info-icon-aligned {
        vertical-align: bottom
    }
</style>
